import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faXmark, faHeartPulse, faExternalLinkAlt, faShieldHalved, faUserSlash, faUserCheck, faExclamationTriangle, faTrophy } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

interface FlaggedActivity {
  scoring_id: number;
  activity_id: number;
  strava_activity_id: number | null;
  sport_type: string;
  activity_name: string;
  started_at_utc: string;
  duration_minutes: number;
  distance_meters: number;
  avg_heart_rate: number;
  max_heart_rate: number;
  athlete_max_hr: number;
  hr_pct: number;
  hr_pct_athlete: number;
  base_points: number;
  challenge_bonus_points: number;
  pr_bonus_points: number;
  total_points: number;
  hr_verified: number;
  flag_reason: string | null;
  strava_url: string | null;
  player_id: number;
  display_name: string;
  zone_breakdown: { zone: number; time_sec: number; points: number }[] | null;
  hr_zones_raw: { min: number; max: number }[] | null;
}

interface FixResult {
  ok: boolean;
  message: string;
  old_base: number;
  new_base: number;
  old_total: number;
  new_total: number;
  recalc_count: number;
  untrusted: boolean;
}

interface UntrustedPlayer {
  id: number;
  display_name: string;
  avatar_url: string;
  level_value: number;
  elo_rating: number;
  is_untrusted: number;
  untrusted_at: string;
  recalc_count: number;
  approved_count: number;
}

interface AdminPanelProps {
  onBack: () => void;
}

export default function AdminPanel({ onBack }: AdminPanelProps) {
  const { token } = useAuth();
  const [activities, setActivities] = useState<FlaggedActivity[]>([]);
  const [highPoints, setHighPoints] = useState<FlaggedActivity[]>([]);
  const [activeTab, setActiveTab] = useState<'flagged' | 'highpoints'>('flagged');
  const [untrusted, setUntrusted] = useState<UntrustedPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);
  const [result, setResult] = useState<FixResult | null>(null);
  const [untrustedBanner, setUntrustedBanner] = useState<string | null>(null);

  const loadData = () => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      api.get('/admin/hr-review', token),
      api.get('/admin/untrusted', token),
      api.get('/admin/high-points', token),
    ]).then(([flagged, untrustedData, highPts]) => {
      if (flagged?.flagged) setActivities(flagged.flagged);
      if (flagged?.error) setError(flagged.error);
      if (untrustedData?.players) setUntrusted(untrustedData.players);
      if (highPts?.items) setHighPoints(highPts.items);
      setLoading(false);
    }).catch(() => {
      setError('Failed to load data');
      setLoading(false);
    });
  };

  useEffect(() => { loadData(); }, [token]);

  const handleApprove = async (scoringId: number) => {
    if (!token || processing) return;
    setProcessing(scoringId);
    setResult(null);
    setUntrustedBanner(null);
    const res = await api.post(`/admin/hr-review/${scoringId}/ok`, {}, token) as any;
    if (res?.ok) {
      setActivities(prev => prev.filter(a => a.scoring_id !== scoringId));
    } else {
      setError(res?.error || 'Failed');
    }
    setProcessing(null);
  };

  const handleFix = async (scoringId: number) => {
    if (!token || processing) return;
    setProcessing(scoringId);
    setResult(null);
    setUntrustedBanner(null);
    const res = await api.post(`/admin/hr-review/${scoringId}/fix`, {}, token) as any;
    if (res?.ok) {
      setResult(res as FixResult);
      setActivities(prev => prev.filter(a => a.scoring_id !== scoringId));
      if (res.untrusted) {
        setUntrustedBanner(`⚠️ Player is now UNTRUSTED after ${res.recalc_count} recalculations – all future workouts will use 0.20 pts/min fallback`);
        loadData(); // Reload untrusted list
      } else if (res.recalc_count >= 3) {
        setUntrustedBanner(`⚠️ ${res.recalc_count}/5 recalculations – player will be untrusted at 5`);
      }
    } else {
      setError(res?.error || 'Failed');
    }
    setProcessing(null);
  };

  const handleRestore = async (playerId: number) => {
    if (!token) return;
    const res = await api.post(`/admin/untrusted/${playerId}/restore`, {}, token) as any;
    if (res?.ok) {
      setUntrusted(prev => prev.filter(p => p.id !== playerId));
    }
  };

  const formatDist = (m: number) => m >= 1000 ? (m / 1000).toFixed(1) + ' km' : Math.round(m) + ' m';

  if (loading) return <div className="admin-panel"><p>Loading...</p></div>;
  if (error) return (
    <div className="admin-panel">
      <button className="back-btn" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
      <div className="admin-error">{error}</div>
    </div>
  );

  return (
    <div className="admin-panel">
      <button className="back-btn" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
      <h2><FontAwesomeIcon icon={faShieldHalved} /> Admin Review</h2>
      <p className="admin-subtitle">
        Activities flagged for review: unrealistic heart rate (&gt;85% of athlete max HR for &gt;60 min) or implausible speed (hike/walk &gt;8 km/h).
      </p>

      {/* Untrusted Players Section */}
      {untrusted.length > 0 && (
        <div className="admin-untrusted-section">
          <h3><FontAwesomeIcon icon={faUserSlash} /> Untrusted Players ({untrusted.length})</h3>
          <p className="admin-subtitle">These players have 5+ recalculated activities. All future workouts use 0.20 pts/min fallback.</p>
          <div className="admin-untrusted-list">
            {untrusted.map(p => (
              <div key={p.id} className="admin-untrusted-card">
                <div className="admin-untrusted-info">
                  <strong>{p.display_name}</strong>
                  <span>Level {p.level_value} · ELO {p.elo_rating}</span>
                  <span>{p.recalc_count} recalculated · {p.approved_count} approved</span>
                  <span className="admin-untrusted-date">Since {new Date(p.untrusted_at).toLocaleDateString('de-AT')}</span>
                </div>
                <button className="admin-btn restore" onClick={() => handleRestore(p.id)}>
                  <FontAwesomeIcon icon={faUserCheck} /> Restore Trust
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'flagged' ? 'active' : ''}`}
          onClick={() => setActiveTab('flagged')}
        >
          <FontAwesomeIcon icon={faHeartPulse} /> Flagged ({activities.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'highpoints' ? 'active' : ''}`}
          onClick={() => setActiveTab('highpoints')}
        >
          <FontAwesomeIcon icon={faTrophy} /> &gt;300 Points ({highPoints.length})
        </button>
      </div>

      {/* Flagged Activities Section */}
      {activeTab === 'flagged' && (
        <>
          <h3><FontAwesomeIcon icon={faHeartPulse} /> Flagged Activities ({activities.length})</h3>

          {result && (
            <div className="admin-result">
              ✅ Recalculated: Base {result.old_base} → <strong>{result.new_base}</strong> pts
              | Total {result.old_total} → <strong>{result.new_total}</strong> pts (Zone-2 fallback)
            </div>
          )}

          {untrustedBanner && (
            <div className="admin-untrusted-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {untrustedBanner}
            </div>
          )}

          {activities.length === 0 ? (
            <div className="admin-empty">🎉 No flagged activities!</div>
          ) : (
            <div className="admin-list">
              {activities.map(act => (
                <div key={act.scoring_id} className={`admin-card ${act.hr_verified ? 'verified' : ''}`}>
                  <div className="admin-card-header">
                    <div className="admin-card-player">
                      <strong>{act.display_name}</strong>
                      <span className="admin-card-date">{new Date(act.started_at_utc).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    </div>
                    {act.strava_url && (
                      <a href={act.strava_url} target="_blank" rel="noopener noreferrer" className="admin-strava-link">
                        Strava <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    )}
                  </div>

                  <div className="admin-card-body">
                    <div className="admin-card-info">
                      <span>{act.activity_name}</span>
                      <span>{act.sport_type}</span>
                      <span>{act.duration_minutes} min</span>
                      <span>{formatDist(act.distance_meters)}</span>
                      {act.flag_reason === 'hr_unrealistic' && (
                        <span className="flag-reason flag-hr">⚠️ Unrealistic HR</span>
                      )}
                      {act.flag_reason === 'speed_implausible' && (
                        <span className="flag-reason flag-speed">⚠️ Implausible speed for {act.sport_type}</span>
                      )}
                    </div>
                    <div className="admin-card-hr">
                      <FontAwesomeIcon icon={faHeartPulse} className="hr-icon" />
                      <span>avg {act.avg_heart_rate} bpm</span>
                      <span>| activity max {act.max_heart_rate}</span>
                      {act.athlete_max_hr > 0 && (
                        <span>| athlete max {act.athlete_max_hr}</span>
                      )}
                      <span className="hr-pct">
                        {act.hr_pct_athlete > 0
                          ? `${act.hr_pct_athlete}% of athlete max`
                          : `${act.hr_pct}% of activity max`
                        }
                      </span>
                    </div>
                    <div className="admin-card-points">
                      <span>Base: {act.base_points}</span>
                      <span>Challenge: {act.challenge_bonus_points}</span>
                      <span>PR: {act.pr_bonus_points}</span>
                      <span className="admin-total">Total: {act.total_points}</span>
                    </div>
                    {act.zone_breakdown && (
                      <div className="admin-card-zones">
                        {act.zone_breakdown.map(z => (
                          <span key={z.zone} className="zone-pill">
                            Z{z.zone}: {Math.round(z.time_sec / 60)}m
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="admin-card-actions">
                    <button
                      className="admin-btn approve"
                      onClick={() => handleApprove(act.scoring_id)}
                      disabled={processing === act.scoring_id}
                    >
                      <FontAwesomeIcon icon={faCheck} /> Correct HR
                    </button>
                    <button
                      className="admin-btn fix"
                      onClick={() => handleFix(act.scoring_id)}
                      disabled={processing === act.scoring_id}
                    >
                      <FontAwesomeIcon icon={faXmark} /> Recalculate (Zone 2)
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* High Points Section */}
      {activeTab === 'highpoints' && (
        <>
          <h3><FontAwesomeIcon icon={faTrophy} /> Activities &gt;300 Points ({highPoints.length})</h3>
          <p className="admin-subtitle">High-scoring activities that may need manual review.</p>

          {highPoints.length === 0 ? (
            <div className="admin-empty">🎉 No activities over 300 points!</div>
          ) : (
            <div className="admin-list">
              {highPoints.map(act => (
                <div key={act.scoring_id} className="admin-card high-point">
                  <div className="admin-card-header">
                    <div className="admin-card-player">
                      <strong>{act.display_name}</strong>
                      <span className="admin-card-date">{new Date(act.started_at_utc).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                    </div>
                    <div className="admin-card-total-badge">🏆 {act.total_points} pts</div>
                    {act.strava_url && (
                      <a href={act.strava_url} target="_blank" rel="noopener noreferrer" className="admin-strava-link">
                        Strava <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    )}
                  </div>

                  <div className="admin-card-body">
                    <div className="admin-card-info">
                      <span>{act.activity_name}</span>
                      <span>{act.sport_type}</span>
                      <span>{act.duration_minutes} min</span>
                      <span>{formatDist(act.distance_meters)}</span>
                      {act.flag_reason && (
                        <span className="flag-reason flag-hr">⚠️ {act.flag_reason}</span>
                      )}
                    </div>
                    <div className="admin-card-hr">
                      <FontAwesomeIcon icon={faHeartPulse} className="hr-icon" />
                      <span>avg {act.avg_heart_rate} bpm</span>
                      <span>| max {act.max_heart_rate}</span>
                      {act.athlete_max_hr > 0 && (
                        <span>| athlete max {act.athlete_max_hr}</span>
                      )}
                      <span className="hr-pct">
                        {act.hr_pct_athlete > 0
                          ? `${act.hr_pct_athlete}% of athlete max`
                          : `${act.hr_pct}% of activity max`
                        }
                      </span>
                    </div>
                    <div className="admin-card-points">
                      <span>Base: {act.base_points}</span>
                      <span>Challenge: {act.challenge_bonus_points}</span>
                      <span>PR: {act.pr_bonus_points}</span>
                      <span className="admin-total">Total: {act.total_points}</span>
                    </div>
                    {act.zone_breakdown && (
                      <div className="admin-card-zones">
                        {act.zone_breakdown.map(z => (
                          <span key={z.zone} className="zone-pill">
                            Z{z.zone}: {Math.round(z.time_sec / 60)}m
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
